import React, { useEffect, useState } from 'react';
import { CheckIcon, LinkIcon } from 'lucide-react';
import { getIntegrationConnections, integrationGoals, integrationsUpdatedEvent, removeIntegrationConnection, saveIntegrationConnection, type IntegrationConnection, type IntegrationGoal, type IntegrationOption } from '../../data/integrations';
import { ConnectIntegrationModal } from './ConnectIntegrationModal';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
export function IntegrationsPanel() {
  const [connections, setConnections] = useState<IntegrationConnection[]>(() => getIntegrationConnections());
  const [active, setActive] = useState<{
    goal: IntegrationGoal;
    option: IntegrationOption;
  } | null>(null);
  useEffect(() => {
    const refresh = () => setConnections(getIntegrationConnections());
    window.addEventListener(integrationsUpdatedEvent, refresh);
    return () => window.removeEventListener(integrationsUpdatedEvent, refresh);
  }, []);
  const connect = (goal: IntegrationGoal, option: IntegrationOption) => {
    saveIntegrationConnection({
      optionId: option.id,
      goalId: goal.id,
      label: option.name,
      connectedAt: new Date().toISOString()
    });
    setConnections(getIntegrationConnections());
    setActive(null);
  };
  const disconnect = (optionId: string) => {
    removeIntegrationConnection(optionId);
    setConnections(getIntegrationConnections());
  };
  return <Card className="p-6 lg:col-span-2" data-id="element-606" data-mp="25mw4ojh6oplh">
      <div className="mb-5" data-id="element-607" data-mp="20o2t4wlgj8ny">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold text-white" data-id="element-608" data-mp="20pw1cgbpy0y2">
          <LinkIcon className="h-4 w-4 text-accent-soft" data-id="element-609" data-mp="1wly0o16qwi2w" /> Integrations
        </h3>
        <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-zinc-500" data-id="element-610" data-mp="1rofq66d2xuss">
          Pick what you want to be accurate. Connect one source per goal — you do not need all of them.
        </p>
      </div>

      <div className="space-y-4" data-id="element-611" data-mp="g8hjua9admv9">
        {integrationGoals.map(goal => {
        const Icon = goal.icon;
        const connectedHere = connections.filter(item => item.goalId === goal.id);
        const isConnected = connectedHere.length > 0;
        return <section key={goal.id} className="rounded-2xl border border-white/[0.07] bg-ink-850 p-4" aria-labelledby={`integration-goal-${goal.id}`} data-id="element-612" data-mp="8pi2091h43e0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-613" data-mp="3v4aw90hs49cg">
                <div className="flex gap-3" data-id="element-614" data-mp="3qndcqgtrklz">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent-soft" data-id="element-615" data-mp="3rgaczpzevqx">
                    <Icon className="h-4.5 w-4.5" data-id="element-616" data-mp="3q5pw112j1po0" />
                  </span>
                  <div data-id="element-617" data-mp="3q5zr332fin0k">
                    <h4 id={`integration-goal-${goal.id}`} className="text-[14px] font-semibold text-white" data-id="element-618" data-mp="3c5ylxbf4stfa">
                      {goal.goal}
                    </h4>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-zinc-500" data-id="element-619" data-mp="3h48j1rkf243j">Turns on: {goal.unlocks}</p>
                  </div>
                </div>
                <span className={isConnected ? 'inline-flex shrink-0 items-center gap-1 rounded-lg bg-grow/10 px-2 py-1 text-[11px] font-medium text-grow' : 'inline-flex shrink-0 items-center rounded-lg bg-white/[0.06] px-2 py-1 text-[11px] font-medium text-zinc-400'} data-id="element-620" data-mp="2skyg38tsmnvf">
                  {isConnected ? <>
                      <CheckIcon className="h-3 w-3" data-id="element-621" data-mp="3bgwygxx257p9" /> Connected
                    </> : 'Not connected'}
                </span>
              </div>

              <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-622" data-mp="3lb6q2de6e1yx">
                {goal.options.map(option => {
              const connection = connections.find(item => item.optionId === option.id);
              return <div key={option.id} className="flex flex-col justify-between rounded-xl border border-white/[0.07] bg-ink-800 p-3" data-id="element-623" data-mp="1sja0typvyrjn">
                      <div data-id="element-624" data-mp="22bfudcvsdh0y">
                        <div className="text-[13px] font-medium text-white" data-id="element-625" data-mp="22c948mq1kmnb">{option.name}</div>
                        <p className="mt-1 text-[11.5px] leading-relaxed text-zinc-500" data-id="element-626" data-mp="26ayi0gezs67h">{option.bestFor}</p>
                      </div>
                      <div className="mt-3" data-id="element-627" data-mp="1ekadqvi0ukpl">
                        {connection ? <div className="flex items-center justify-between gap-2" data-id="element-628" data-mp="1im5g83bov44y">
                            <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-grow" data-id="element-629" data-mp="1ioi6d3rlokt3">
                              <CheckIcon className="h-3.5 w-3.5" data-id="element-630" data-mp="2u1c8i2hxhwzy" /> Connected
                            </span>
                            <button type="button" onClick={() => disconnect(option.id)} className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-zinc-500 transition-colors hover:bg-danger/10 hover:text-danger" data-id="element-631" data-mp="2z05ezayssnqm">
                              Disconnect
                            </button>
                          </div> : <Button variant="secondary" size="sm" onClick={() => setActive({
                    goal,
                    option
                  })} data-id="element-632" data-mp="2vb4hx2e3jqdd">
                            Connect
                          </Button>}
                      </div>
                    </div>;
            })}
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-zinc-600" data-id="element-633" data-mp="2ce33kqynkdwr">{goal.freshness}</p>
            </section>;
      })}
      </div>

      <ConnectIntegrationModal goal={active?.goal ?? null} option={active?.option ?? null} onConnect={connect} onClose={() => setActive(null)} data-id="element-634" data-mp="enryajuikk37" />
    </Card>;
}